import { type ReactNode } from 'react';
import { formatServiceTime } from '@/utils/formatServiceTime';
import type { Order, ProgressNode } from '@/types/order';
import { OrderCleanItemsSection } from '@/components/order/OrderCleanItemsSection';
import { StaffHourAllocation } from '@/components/order/StaffHourAllocation';
import { isNavigableAddress, openGoogleMapsNavigation } from '@/lib/openGoogleMaps';
import {
  canUserMarkServiceCompleted,
  formatDurationHours,
  formatOrderDurationHours,
  formatOrderTotalPrice,
  resolveOrderServiceDuration,
} from '@/api/modules/order';
import { getOrderStatusMeta } from '@/constants/calendarStatus';
import { formatGstPercentInput, orderGstRateToDecimal } from '@/lib/orderPricing';

interface OrderDetailViewProps {
  order: Order;
  currentUserId?: string | null;
  canViewPrivacy?: boolean;
  onServiceComplete?: (orderId: string) => void;
  serviceCompleteSubmitting?: boolean;
  onAssign?: (order: Order) => void;
  onOrderOperation?: (order: Order) => void;
  onApplyInvoice?: (order: Order) => void;
  onConfirmPayment?: (order: Order) => void;
  confirmPaymentSubmitting?: boolean;
  onReorder?: (order: Order) => void;
  confirmPaymentLabel?: string;
}

function formatOrderGstLabel(gstRate: number | undefined): string {
  const gstDecimal = orderGstRateToDecimal(gstRate);
  if (gstDecimal === null) return '—';
  return `${formatGstPercentInput(gstDecimal)}%`;
}

export default function OrderDetailView({
  order,
  currentUserId = null,
  canViewPrivacy = false,
  onServiceComplete,
  serviceCompleteSubmitting = false,
  onAssign,
  onOrderOperation,
  onApplyInvoice,
  onConfirmPayment,
  confirmPaymentSubmitting = false,
  onReorder,
  confirmPaymentLabel,
}: OrderDetailViewProps) {
  const cfg = getOrderStatusMeta(order.status);
  const canMarkComplete = canUserMarkServiceCompleted(order, currentUserId);
  const showInvoice = Boolean(onApplyInvoice)
    && (order.status === 'service_done' || order.status === 'completed');
  const teamTitle = order.teamName?.trim() || 'Team';
  const orderRef = order.id.trim().startsWith('#') ? order.id.trim() : `#${order.id.trim()}`;
  const fee = typeof order.totalPriceWithGst === 'number'
    ? order.totalPriceWithGst
    : order.totalPrice;

  return (
    <div className="flex flex-col min-h-0">
      <div className="px-4 pb-3">
        <div className="flex items-center gap-3 bg-white rounded-lg px-3.5 py-2.5 border border-gray-200">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm font-semibold text-gray-800 truncate">{teamTitle}</span>
              <span className="text-xs font-medium text-gray-400 flex-shrink-0 tabular-nums">
                {orderRef}
              </span>
            </div>
            <div className="text-gray-400 text-[11px] truncate mt-0.5">
              Created {order.create_time ? formatServiceTime(order.create_time) : '—'}
            </div>
          </div>
          <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap border flex-shrink-0 ${cfg.badgeClass}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${cfg.dotClass}`} />
            {cfg.label}
          </span>
        </div>
      </div>

      <div className="px-4 space-y-3 pb-6">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              {canViewPrivacy ? 'Customer Info' : 'Service Location'}
            </span>
          </div>
          <div className="px-4 py-3 space-y-2.5">
            {canViewPrivacy ? (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                <MetaField icon="ri-user-3-line" label="Name" value={order.customer.name} />
                {order.customer.phone && order.customer.phone !== '—' ? (
                  <MetaField icon="ri-phone-line" label="Phone" value={order.customer.phone} />
                ) : null}
                {order.customer.email && order.customer.email !== '—' ? (
                  <MetaField icon="ri-mail-line" label="Email" value={order.customer.email} />
                ) : null}
              </div>
            ) : null}
            <InfoRow
              icon="ri-map-pin-2-line"
              color="text-green-500"
              label="Address"
              value={order.customer.address}
              multiline
              trailing={
                isNavigableAddress(order.customer.address) ? (
                  <button
                    type="button"
                    onClick={() => openGoogleMapsNavigation(order.customer.address)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white cursor-pointer hover:bg-green-50 transition-colors"
                    aria-label="Open address in Google Maps"
                  >
                    <i className="ri-navigation-line text-green-500 text-sm" aria-hidden />
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Service Details</span>
          </div>
          <div className="px-4 py-3 space-y-2.5">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <MetaField label="Time" value={formatServiceTime(order.service_time)} />
              {canViewPrivacy ? (
                <MetaField
                  label="Work Duration"
                  value={formatOrderDurationHours(resolveOrderServiceDuration(order.serviceDuration, order.cleanItems))}
                />
              ) : (
                <MetaField
                  label="Total Service Duration"
                  value={formatDurationHours(resolveOrderServiceDuration(order.serviceDuration, order.cleanItems))}
                />
              )}
              {canViewPrivacy ? (
                <>
                  <MetaField label="GST" value={formatOrderGstLabel(order.gstRate)} />
                  <MetaField label="Ex-GST" value={formatOrderTotalPrice(order.totalPrice)} />
                  <MetaField label="Inc-GST" value={formatOrderTotalPrice(fee)} />
                </>
              ) : null}
            </div>
            <OrderCleanItemsSection order={order} showDuration={canViewPrivacy} />
            <StaffHourAllocation order={order} markPersonalMts />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Progress</span>
          </div>
          <div className="px-4 py-3">
            <OrderProgressFlow nodes={order.progress} />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Order Info</span>
          </div>
          <div className="px-4 py-3">
            {order.notes ? (
              <p className="text-gray-700 text-xs font-medium leading-relaxed whitespace-pre-wrap">{order.notes}</p>
            ) : (
              <p className="text-gray-400 text-xs">No additional information</p>
            )}
          </div>
        </div>

        {onAssign ? (
          <button
            type="button"
            onClick={() => onAssign(order)}
            className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg bg-green-600 text-white font-semibold text-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line" />Assign
          </button>
        ) : null}

        {onOrderOperation ? (
          <button
            type="button"
            onClick={() => onOrderOperation(order)}
            className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold text-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-price-tag-3-line" />Price & Hours
          </button>
        ) : null}

        {canMarkComplete ? (
          <button
            type="button"
            disabled={serviceCompleteSubmitting}
            onClick={() => onServiceComplete?.(order.id)}
            className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg bg-green-600 text-white font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            <i className={serviceCompleteSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-checkbox-circle-line'} />
            {serviceCompleteSubmitting ? 'Saving…' : 'Service Completed'}
          </button>
        ) : null}

        {showInvoice ? (
          <button
            type="button"
            onClick={() => onApplyInvoice?.(order)}
            className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold text-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bill-line" />Apply Invoice
          </button>
        ) : null}

        {onReorder ? (
          <button
            type="button"
            onClick={() => onReorder(order)}
            className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold text-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line" />Reorder
          </button>
        ) : null}

        {onConfirmPayment ? (
          <button
            type="button"
            disabled={confirmPaymentSubmitting}
            onClick={() => onConfirmPayment(order)}
            className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg bg-teal-600 text-white font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            <i className={confirmPaymentSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-secure-payment-line'} />
            {confirmPaymentSubmitting ? 'Saving…' : (confirmPaymentLabel || 'Confirm Payment')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function OrderProgressFlow({ nodes }: { nodes: ProgressNode[] }) {
  return (
    <div>
      {nodes.map((node, idx) => {
        const isLast = idx === nodes.length - 1;
        const lineDone = !isLast && node.completed && nodes[idx + 1].completed;
        return (
          <div key={node.id} className="flex gap-2.5">
            <div className="flex flex-col items-center flex-shrink-0 w-4">
              <div className={`w-4 h-4 flex items-center justify-center rounded-full border ${
                node.completed ? 'bg-green-600 border-green-600' : 'bg-white border-gray-200'
              }`}>
                {node.completed
                  ? <i className="ri-check-line text-white text-xs" />
                  : <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />}
              </div>
              {!isLast ? (
                <div className={`w-px flex-1 min-h-2 my-0.5 ${lineDone ? 'bg-green-300' : 'bg-gray-200'}`} />
              ) : null}
            </div>
            <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-2.5'}`}>
              <div className={`text-xs font-medium leading-4 ${node.completed ? 'text-gray-700' : 'text-gray-400'}`}>
                {node.title}
              </div>
              <div className="text-[11px] text-gray-400 mt-0.5">{node.time}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MetaField({
  label, value, icon, iconColor = 'text-green-500',
}: {
  label: string; value: string; icon?: string; iconColor?: string;
}) {
  return (
    <div className="min-w-0 flex items-start gap-2 basis-[calc(50%-0.5rem)]">
      {icon ? (
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${icon} ${iconColor} text-xs`} />
        </div>
      ) : null}
      <div className="min-w-0">
        <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{label}</div>
        <div className="text-gray-800 text-xs font-medium leading-snug break-words">{value}</div>
      </div>
    </div>
  );
}

function InfoRow({
  icon, color, label, value, multiline, trailing,
}: {
  icon: string; color: string; label: string; value: string; multiline?: boolean; trailing?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className={`${icon} ${color} text-xs`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-0.5">{label}</div>
        <div className={`text-gray-700 text-xs font-medium leading-relaxed ${multiline ? '' : 'truncate'}`}>{value}</div>
      </div>
      {trailing ? <div className="flex-shrink-0 self-center">{trailing}</div> : null}
    </div>
  );
}
